import easyfyAxios from '@shared/easyfy.axios';

namespace Easyfy.Web.Services.Prescription {

  // #region BaseURL Constants
  const BASE_URLS = {
    PRESCRIPTION: '/prescription',
    API: '/api'
  } as const;

  const ENDPOINTS = {
    MY_PRESCRIPTIONS: `${BASE_URLS.PRESCRIPTION}/myprescriptions`,
    MY_PROXIES: `${BASE_URLS.PRESCRIPTION}/myproxies`,
    MY_SUBVENTIONS: `${BASE_URLS.PRESCRIPTION}/mysubventions`,
    MY_DRUG_LISTS: `${BASE_URLS.PRESCRIPTION}/mydruglists`,
    PRESCRIPTION_CART: `${BASE_URLS.API}/prescriptioncart`,
    CUSTOMER_SESSION: `${BASE_URLS.API}/customersession`
  } as const;

  // #endregion

  // #region Interfaces
  export interface PrescriptionRequest {
    selectedUserSsn: string;
    proxyPersonName: string;
    isChild: boolean;
    sortKey: PrescriptionListSortOptions;
    sortDirection: SortDirection;
  }

  export interface SsnModel {
    ssn: string
  }

  export interface SubventionDetailsRequest {
    selectedUserSsn: string;
    proxyPersonName: string;
    displayAmountUntilFree?: boolean;
    displayCurrentPeriodCost?: boolean;
    displayHeader?: boolean;
    displaySubventionChart?: boolean;
  }

  export interface PrescriptionDetailsRequest {
    selectedUserSsn: string;
    ordinationId: Number;
    fullName: string;
  }

  export interface AnimalPrescriptionListRequest {
    sortKey: PrescriptionListSortOptions;
    sortDirection: SortDirection;
  }

  export interface AnimalPrescriptionDetailsRequest {
    prescriptionId: string;
    recipeRowId: string;
  }

  export interface PrescriptionBuyCommand {
    ordinationId: string;
    nplPackId: string;
    price: Number;
    prescriptionPurchasable: boolean;
    drugPurchasable: boolean;
    customPvPrice: Number;
    quantity: Number;
    varuNr: string;
    receptRadId: string;
    withBenefits: boolean;
    hash: string;
    userId: string;
    fullName: string;
  }

  export interface GiveProxyCommand {
    consent: boolean;
    ssn: string;
    isIndefiniteDate: boolean;
    pickedDate?: Date;
  }

  export interface AddChildProxyCommand {
    ssn: string;
    savePersistent: boolean;
  }

  export interface RemoveProxyCommand {
    ssn: string;
    name: string;
    isChild: boolean;
    isReceived: boolean;
  }

  export interface ApproveAwaitingProxyCommand {
    ssn: string;
    consent: boolean;
  }

  export interface DeleteCartCommand {
    cartId: string;
  }

  export interface GetCartCommand {
    cartId: string;
  }

  export interface DeleteCartRowCommand {
    cartId: string;
    prescriptionId: number;
    receptRadId: string;
    prescriptionWithBenefit: boolean;
    price: number;
    varunr: string;
    nplPackId: string;
    renderMiniCart?: boolean;
  }

  export interface PharmaceuticalAdviceModel {
    isChecked: boolean;
  }

  export interface FassDocumentRequest {
    id: string;
    isNplPackId: boolean;
    isAnimal: boolean;
  }

  export interface IncludePrescriptionSpecification {
    cartId: string;
    includePrescriptionSpecification: boolean;
  }

  export interface GetRxCommentCommand {
    cartId: string;
  }

  export interface SaveRxCommentCommand {
    cartId: string;
    comment: string;
  }
  // #endregion

  // #region Enums
  export enum PrescriptionListSortOptions {
    Date = 'Date',
    Name = 'Name',
    ActiveSubstance = 'ActiveSubstance'
  }

  export enum SortDirection {
    Descending = 'Descending',
    Ascending = 'Ascending'
  }
  // #endregion

  // #region MyPrescriptions

  export function parseSortDirection(value: string): SortDirection | undefined {
    return Object.values(SortDirection).find(v => v === value) as SortDirection | undefined;
  }

  export function parseSortOption(value: string): PrescriptionListSortOptions | undefined {
    return Object.values(PrescriptionListSortOptions).find(v => v === value) as PrescriptionListSortOptions | undefined;
  }

  export const GetPrescriptionPageUrl = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=myUrl`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetProxies = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=proxies`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetPrescriptionList = async (
    data: PrescriptionRequest,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=prescriptionsForUser`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };
  
  export const GetExpiredPrescriptionsList = async (
    data: PrescriptionRequest,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=expiredPrescriptions`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const GetAnimalPrescriptionList = async (data: AnimalPrescriptionListRequest): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=animalPrescriptions`;
    return easyfyAxios.post(
      url,
      data
    );
  };

  export const GetPrescriptionDetails = async (
    data: PrescriptionDetailsRequest
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=prescriptionDetails&ssn=${data.selectedUserSsn}&ordinationId=${data.ordinationId}&fullName=${data.fullName}`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetAnimalPrescriptionDetails = async (
    data: AnimalPrescriptionDetailsRequest
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=animalPrescriptionDetails&prescriptionId=${data.prescriptionId}&recipeRowId=${data.recipeRowId}`;
    return easyfyAxios.get(
      url
    );
  };

  export const PostPrescriptionBuy = async (
    data: PrescriptionBuyCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=updateCart`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const GetAddChildModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=addChildModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetAnimalConsentModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=animalConsentModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetPharmaceuticalAdviceModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=pharmaceuticalAdviceModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetExchangeInfoModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=exchangeInfoModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetHowToModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=howToModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const PostAddChildProxy = async (
    data: AddChildProxyCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=addChildProxy`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const PostConsentAnimal = async (
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=consentAnimal`;
    return easyfyAxios.post(
      url,
      null,
      token
    );
  };

  export const GetFassDocumentModal = async (
    header: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=fassDocumentModal&header=${header}`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetFassDocument = async (
    data: FassDocumentRequest
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PRESCRIPTIONS}?handler=fassdocument&id=${data.id}&isNplPackId=${data.isNplPackId}&isAnimal=${data.isAnimal}`;
    return easyfyAxios.get(
      url
    );
  };

  // #endregion

  // #region MySubventions

  export const GetSubventionProxies = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_SUBVENTIONS}?handler=subventionProxies`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetSubventionDetails = async (
    data: SubventionDetailsRequest,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_SUBVENTIONS}?handler=subventionDetails`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  // #endregion

  // #region MyProxies

  export const GetProxyList = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=proxyList`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetRemoveProxyModal = async (
    data: RemoveProxyCommand
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=removeProxyModal&ssn=${data.ssn}&name=${data.name}&isChild=${data.isChild}&isReceived=${data.isReceived}`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetApproveAwaitingProxyModal = async (
    data: ApproveAwaitingProxyCommand
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=approveAwaitingModal&ssn=${data.ssn}`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetGiveProxyModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=giveProxyModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const PostRemoveProxy = async (
    data: RemoveProxyCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=removeProxy`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const PostApproveAwaitingProxy = async (
    data: ApproveAwaitingProxyCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=approveAwaitingProxy`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const PostGiveProxy = async (
    data: GiveProxyCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.MY_PROXIES}?handler=giveProxy`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };


  // #endregion

  // #region PrescriptionCart

  export const DeleteCart = async (
    data: DeleteCartCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=delete`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const GetCart = async (
    data: GetCartCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=get`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const DeleteCartRow = async (
    data: DeleteCartRowCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=deletecartrow`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const SetPharmaceuticalAdvice = async (
    data: PharmaceuticalAdviceModel,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=setpharmaceuticaladvice`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const SaveCustomerComment = async (
    data: SaveRxCommentCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=savecustomercomment`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const GetCustomerComment = async (
    data: GetRxCommentCommand,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=getcustomercomment`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  export const IncludePrescriptionSpecification = async (
    data: IncludePrescriptionSpecification,
    token: string
  ): Promise<any> => {
    const url = `${ENDPOINTS.PRESCRIPTION_CART}?handler=includeprescriptionspecification`;
    return easyfyAxios.post(
      url,
      data,
      token
    );
  };

  // #endregion

  // #region MyDrugLists

  export const GetDrugLists = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_DRUG_LISTS}?handler=drugLists`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetDrugListModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_DRUG_LISTS}?handler=drugListModal`;
    return easyfyAxios.get(
      url
    );
  };

  export const GetSavedPrescriptionsModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.MY_DRUG_LISTS}?handler=savedPrescriptionsModal`;
    return easyfyAxios.get(
      url
    );
  };

  // #endregion

  // #region CustomerSession

  export const GetLogoutModal = async (): Promise<any> => {
    const url = `${ENDPOINTS.CUSTOMER_SESSION}?handler=logoutModal`;
    return easyfyAxios.get(
      url
    );
  };

  // #endregion

}

export default Easyfy.Web.Services.Prescription;
